import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../auth/useAuth0';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronLeft,
    faChevronRight,
    faPlus,
    faChevronDown,
    faChevronUp,
    faEye,
    faEyeSlash,
} from '@fortawesome/free-solid-svg-icons';
import { useBusinessContext } from '../../../context/BusinessContext';
import { usePermissions } from '../../../context/PermissionsContext';
import { useAlert } from '../../../context/alert/AlertContext';
import { useValueVisibility } from '../../../context/ValueVisibilityContext';
import LoadingComponent from '../../commons/LoadingComponent';
import Drawer from '../../commons/drawer/Drawer';
import ConfirmModal from '../../commons/confirm-modal/ConfirmModal';
import MoneyInput from '../../commons/money-input/MoneyInput';
import { currencyCode, formatMoney } from '../../../util/money';
import {
    getFinanceEntries,
    getFinanceSummary,
    saveFinanceEntry,
    deleteFinanceEntry,
} from '../../../service/finance/finance-service';
import {
    FinanceEntry,
    FinanceEntryInput,
    FinanceEntryType,
    FinanceMonth,
    FinancePeriodicity,
    FinanceSummary,
} from '../../../model/finance/Finance';
import { Service } from '../../../model/service/Service';
import { Appointment } from '../../../model/appointment/Appointment';
import { Transaction, TransactionStatus } from '../../../model/payment/Transaction';
import { CommissionRecord } from '../../../model/teammember/Commission';
import { TeamMember } from '../../../model/teammember/TeamMember';
import { Event as EventModel } from '../../../model/event/Event';
import { getServicesByBusinessId } from '../../../service/service/service-service';
import { getAppointmentsByBusinessAndRange } from '../../../service/appointment/appointment-service';
import { getEventsByBusinessAndRange } from '../../../service/event/event-service';
import { getTransactions, getCommissionDetails, markReceivableReceived } from '../../../service/payment/transaction-service';
import { useConfirm } from '../../../context/confirm/ConfirmContext';
import { findBusinessDetailsByBusinessId } from '../../../service/business/business-service';
import { getTeamMembersByBusinessId } from '../../../service/teammember/team-member-service';
import { getSalaryPayments } from '../../../service/payment/salary-service';
import { SalaryPayment } from '../../../model/payment/Salary';
import DonutChart, { DonutSegment, DONUT_PALETTE } from './DonutChart';
import './FinanceComponent.css';

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

const EXPENSE_CATEGORY_PRESETS = [
    'Aluguel', 'Luz', 'Água', 'Internet', 'Funcionários',
    'Produtos', 'Marketing', 'Impostos', 'Equipamentos', 'Manutenção', 'Outros',
];

const CREDIT_CATEGORY_PRESETS = [
    'Vendas', 'Serviços', 'Gorjetas', 'Parcerias', 'Outros',
];

const INVESTMENT_CATEGORY_PRESETS = [
    'Equipamentos', 'Reforma', 'Tecnologia', 'Estoque', 'Marketing', 'Outros',
];

/** Short Portuguese month names. */
const PT_MONTH = [
    'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
    'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];

const PT_MONTH_FULL = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function monthLabel(yyyyMM: string): string {
    const [, mm] = yyyyMM.split('-');
    return PT_MONTH[Number(mm) - 1] ?? yyyyMM;
}

function formatDate(iso: string | null | undefined): string {
    // iso is "yyyy-MM-dd"; legacy entries may have no date.
    if (!iso) return '—';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
}

function isCurrentMonth(yyyyMM: string, year: number): boolean {
    const now = new Date();
    return now.getFullYear() === year && now.getMonth() + 1 === Number(yyyyMM.split('-')[1]);
}

/**
 * Occurrence step in months per periodicity.
 */
const STEP: Record<FinancePeriodicity, number> = {
    ONCE: 0,
    MONTHLY: 1,
    QUARTERLY: 3,
    SEMIANNUAL: 6,
    ANNUAL: 12,
};

/**
 * entryAmountInPeriod — replicates the backend occurrence rule exactly.
 *
 * For a given entry, returns the contribution to a period:
 *   - selectedMonth set (1-12): amount if the entry occurs in (year, selectedMonth), else 0.
 *   - selectedMonth null (Ano todo): amount × count of months Jan..Dec of `year` in which it occurs.
 *
 * Occurrence rule:
 *   start = (startDate.year, startDate.month)
 *   end   = endDate ? (endDate.year, endDate.month) : none
 *   step  = STEP[periodicity]
 *   ONCE: contributes iff start.year==Y && start.month==M
 *   Recurring: diff=(Y-start.year)*12+(M-start.month);
 *              contributes iff diff>=0 AND (no end OR (Y,M)<=(end.year,end.month)) AND diff%step==0
 */
function entryOccursInMonth(entry: FinanceEntry, year: number, month: number): boolean {
    // Legacy entries (created before start_date existed) have null startDate —
    // mirror the backend fallback: treat them as starting in January of the year.
    let sy = year, sm = 1;
    if (entry.startDate) {
        [sy, sm] = entry.startDate.split('-').map(Number);
    }
    if (entry.periodicity === 'ONCE') {
        // A ONCE entry with no date can't be placed — show it in January as a safe default.
        return sy === year && sm === month;
    }
    const diff = (year - sy) * 12 + (month - sm);
    if (diff < 0) return false;
    const step = STEP[entry.periodicity];
    if (diff % step !== 0) return false;
    if (entry.endDate) {
        const [ey, em] = entry.endDate.split('-').map(Number);
        if (year > ey || (year === ey && month > em)) return false;
    }
    return true;
}

export function entryAmountInPeriod(
    entry: FinanceEntry,
    year: number,
    selectedMonth: number | null
): number {
    if (!entry.active) return 0;
    if (selectedMonth !== null) {
        return entryOccursInMonth(entry, year, selectedMonth) ? entry.amount : 0;
    }
    // Ano todo — sum over all 12 months
    let total = 0;
    for (let m = 1; m <= 12; m++) {
        if (entryOccursInMonth(entry, year, m)) total += entry.amount;
    }
    return total;
}

/** 12 placeholder months for an empty / still-loading summary. */
function emptyMonths(year: number): FinanceMonth[] {
    return Array.from({ length: 12 }, (_, i) => ({
        month: `${year}-${String(i + 1).padStart(2, '0')}`,
        gross: 0,
        credits: 0,
        revenue: 0,
        taxes: 0,
        commissions: 0,
        salaries: 0,
        expenses: 0,
        investments: 0,
        net: 0,
        accumulatedBalance: 0,
    }));
}

const EMPTY_SUMMARY = (year: number): FinanceSummary => ({
    year,
    months: emptyMonths(year),
    yearGross: 0,
    yearCredits: 0,
    yearRevenue: 0,
    yearTaxes: 0,
    yearCommissions: 0,
    yearSalaries: 0,
    yearExpenses: 0,
    yearInvestments: 0,
    yearNet: 0,
    endBalance: 0,
    projectedYearRevenue: 0,
    projectedYearNet: 0,
});

/**
 * getPeriodData — returns a single FinanceMonth-like aggregate for the selected period.
 * Month mode: summary.months[selectedMonth-1].
 * Year mode (Ano todo): sums additive fields; accumulatedBalance = endBalance.
 */
function getPeriodData(summary: FinanceSummary, selectedMonth: number | null): FinanceMonth {
    if (selectedMonth !== null) {
        return summary.months[selectedMonth - 1] ?? emptyMonths(summary.year)[0];
    }
    return {
        month: String(summary.year),
        gross: summary.yearGross,
        credits: summary.yearCredits,
        revenue: summary.yearRevenue,
        taxes: summary.yearTaxes,
        commissions: summary.yearCommissions,
        salaries: summary.yearSalaries,
        expenses: summary.yearExpenses,
        investments: summary.yearInvestments,
        net: summary.yearNet,
        accumulatedBalance: summary.endBalance,
    };
}

/** Transaction statuses that represent real money received. */
const FATURAMENTO_STATUSES = new Set<TransactionStatus>(['SUCCEEDED', 'PARTIALLY_REFUNDED', 'REFUNDED']);

type FinanceTab = 'overview' | 'ledger' | 'services' | 'events' | 'receivables';
type LedgerFilter = 'all' | 'CREDIT' | 'EXPENSE' | 'INVESTMENT' | 'COMMISSION' | 'SALARY' | 'SERVICE';

// ---------------------------------------------------------------------------
// Unified ledger row (combines all sources)
// ---------------------------------------------------------------------------

/**
 * Source of a ledger row:
 *   MANUAL    — FinanceEntry (CREDIT / EXPENSE / INVESTMENT)
 *   SERVICE   — Transaction (Serviço pago → ENTRADA)
 *   COMMISSION — CommissionRecord with paidAt (Comissão paga → SAÍDA)
 *   SALARY    — SalaryPayment (Salário pago → SAÍDA, month mode)
 *   TAX_SUMMARY — aggregated platformFee line (Ano todo mode only)
 *   SERVICE_SUMMARY — aggregated faturamento line (Ano todo mode only)
 *   COMMISSION_SUMMARY — aggregated paid-commission line (Ano todo mode only)
 *   SALARY_SUMMARY — aggregated paid-salary line (Ano todo mode only)
 */
type LedgerRowSource = 'MANUAL' | 'SERVICE' | 'COMMISSION' | 'SALARY' | 'TAX_SUMMARY' | 'SERVICE_SUMMARY' | 'COMMISSION_SUMMARY' | 'SALARY_SUMMARY';

interface LedgerRow {
    /** Unique key for React rendering. */
    key: string;
    source: LedgerRowSource;
    /** ISO "yyyy-MM-dd" for the date column — null for summary rows. */
    date: string | null;
    /** Displayed in the Tipo column. */
    typeChip: 'SERV' | 'COMISSION' | 'SALARY' | 'CREDIT' | 'EXPENSE' | 'INVESTMENT';
    /** Main description line. */
    description: string;
    /** Optional sub-label (category + periodicity for manual; badge count for summaries). */
    subLabel?: string;
    /** The effective amount to display (always positive; sign determined by the chip/color). */
    amount: number;
    /** True = line adds to ENTRADAS. False = adds to SAÍDAS. Investments are tracked separately. */
    isEntrada: boolean;
    /** True only for INVESTMENT manual entries. */
    isInvestment: boolean;
    /** For MANUAL rows only — allows row click → drawer. */
    manualEntry?: FinanceEntry;
    /** True = comes from an automated source → non-clickable, muted "auto" tag. */
    isAuto: boolean;
    /** Inativo flag for manual rows. */
    isInactive: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FinanceComponent: React.FC = () => {
    const { t } = useTranslation();
    const { getAccessTokenSilently } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const { setAlert } = useAlert();
    const { hidden, toggle: toggleVisibility } = useValueVisibility();

    const businessId = selectedBusiness?.id ? String(selectedBusiness.id) : '';
    const currency = currencyCode(selectedBusiness?.currency);

    /** Company default tax rate (%) — drives the informational "estimated taxes"
     *  card. This is an ESTIMATE outside the cash flow (not a real expense). */
    const [companyTaxRate, setCompanyTaxRate] = useState<number | null>(null);
    useEffect(() => {
        if (!businessId) { setCompanyTaxRate(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const d = await findBusinessDetailsByBusinessId(Number(businessId), token).catch(() => null);
                if (!cancelled) setCompanyTaxRate(d?.retailsSalesTaxRate ?? null);
            } catch { /* non-critical */ }
        })();
        return () => { cancelled = true; };
    }, [businessId, getAccessTokenSilently]);

    // ---------- Global filters ----------
    const [year, setYear] = useState<number>(new Date().getFullYear());
    /** 1-12 = specific month, null = whole year */
    const [selectedMonth, setSelectedMonth] = useState<number | null>(null);

    // ---------- Finance data ----------
    const [entries, setEntries] = useState<FinanceEntry[]>([]);
    const [summary, setSummary] = useState<FinanceSummary>(EMPTY_SUMMARY(year));
    const [loading, setLoading] = useState(true);

    // ---------- Services, Appointments & Transactions (tab Serviços + Lançamentos) ----------
    const [services, setServices] = useState<Service[]>([]);
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [events, setEvents] = useState<EventModel[]>([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [servicesLoading, setServicesLoading] = useState(false);

    // ---------- Commissions + TeamMembers + Salaries (tab Lançamentos) ----------
    const [commissions, setCommissions] = useState<CommissionRecord[]>([]);
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
    const [salaryPayments, setSalaryPayments] = useState<SalaryPayment[]>([]);
    const [ledgerDataLoading, setLedgerDataLoading] = useState(false);

    // ---------- Tab ----------
    const [activeTab, setActiveTab] = useState<FinanceTab>('overview');
    // "Contas a receber" (mark a convênio receivable received) is a PAYMENTS action —
    // the backend gates markReceivableReceived on PAYMENTS. Only show the tab to a
    // PAYMENTS holder so the button never appears where the action would be denied.
    const { canView } = usePermissions();
    const canReceivables = canView('PAYMENTS');

    // ---------- DRE expandable rows ----------
    const [dreExpandExpenses, setDreExpandExpenses] = useState(false);
    const [dreExpandCredits, setDreExpandCredits] = useState(false);
    const [dreExpandInvestments, setDreExpandInvestments] = useState(false);

    // ---------- Receivables (contas a receber — convênio) ----------
    const [receivables, setReceivables] = useState<Transaction[]>([]);
    const [receivablesLoading, setReceivablesLoading] = useState(false);
    const [markBusyId, setMarkBusyId] = useState<string | null>(null);
    const confirm = useConfirm();

    // ---------- Ledger filter ----------
    const [ledgerFilter, setLedgerFilter] = useState<LedgerFilter>('all');
    // Reset to 'all' when switching away so we don't end up on a stale filter.
    const setLedgerFilterSafe = (f: LedgerFilter) => setLedgerFilter(f);

    // ---------- Drawer ----------
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [draft, setDraft] = useState<FinanceEntryInput | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [amountStr, setAmountStr] = useState('');
    const [categoryCustom, setCategoryCustom] = useState(false);
    const [categoryCustomText, setCategoryCustomText] = useState('');

    // ---------- Delete modal ----------
    const [deleteTarget, setDeleteTarget] = useState<FinanceEntry | null>(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    // ---------- Data loading ----------

    const reload = useCallback(async () => {
        if (!businessId) return;
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const [fetchedEntries, fetchedSummary] = await Promise.all([
                getFinanceEntries(businessId, token),
                getFinanceSummary(businessId, year, token),
            ]);
            setEntries(fetchedEntries);
            setSummary(fetchedSummary);
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('finance.alerts.failLoad'));
            setSummary(EMPTY_SUMMARY(year));
        } finally {
            setLoading(false);
        }
    }, [businessId, year, getAccessTokenSilently, setAlert]);

    useEffect(() => { reload(); }, [reload]);

    /** ISO range helpers shared by both Serviços and Lançamentos loaders. */
    const periodIsoRange = useMemo((): { startIso: string; endIso: string } => {
        const startIso = selectedMonth !== null
            ? `${year}-${String(selectedMonth).padStart(2, '0')}-01T00:00:00`
            : `${year}-01-01T00:00:00`;
        const endIso = selectedMonth !== null
            ? (() => {
                const d = new Date(year, selectedMonth, 1);
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01T00:00:00`;
            })()
            : `${year + 1}-01-01T00:00:00`;
        return { startIso, endIso };
    }, [year, selectedMonth]);

    // Load services + appointments + transactions when Serviços tab is active
    useEffect(() => {
        if (activeTab !== 'services' || !businessId) return;
        const load = async () => {
            setServicesLoading(true);
            try {
                const token = await getAccessTokenSilently();
                const { startIso, endIso } = periodIsoRange;
                const [svcResult, apptResult, txPage] = await Promise.all([
                    getServicesByBusinessId(businessId, token),
                    getAppointmentsByBusinessAndRange(businessId, startIso, endIso, token),
                    getTransactions(businessId, { start: startIso, end: endIso, page: 1, perPage: 10000 }, token),
                ]);
                setServices(svcResult);
                setAppointments(apptResult);
                setTransactions(txPage.items);
            } catch (e) {
                setAlert('danger', e instanceof Error ? e.message : t('finance.alerts.failLoadServices'));
            } finally {
                setServicesLoading(false);
            }
        };
        load();
    }, [activeTab, businessId, periodIsoRange, getAccessTokenSilently, setAlert]);

    // Load events + transactions when the Eventos tab is active. Event payments
    // carry eventId (never an appointment serviceId), so they belong here — not
    // in the "Avulso" bucket of the Serviços tab.
    useEffect(() => {
        if (activeTab !== 'events' || !businessId) return;
        const load = async () => {
            setEventsLoading(true);
            try {
                const token = await getAccessTokenSilently();
                const { startIso, endIso } = periodIsoRange;
                // Transactions are filtered by PAYMENT date (the period); events are
                // looked up only to resolve titles, and a payment may be for an event
                // held well outside the period — so fetch events over a wide window.
                // Match the backend's LocalDateTime format (no trailing Z / millis).
                const wideStart = new Date(new Date(startIso).getTime() - 400 * 86400000).toISOString().slice(0, 19);
                const wideEnd = new Date(new Date(endIso).getTime() + 400 * 86400000).toISOString().slice(0, 19);
                const [evResult, txPage] = await Promise.all([
                    getEventsByBusinessAndRange(businessId, wideStart, wideEnd, token).catch(() => [] as EventModel[]),
                    getTransactions(businessId, { start: startIso, end: endIso, page: 1, perPage: 10000 }, token),
                ]);
                setEvents(evResult);
                setTransactions(txPage.items);
            } catch (e) {
                setAlert('danger', e instanceof Error ? e.message : t('finance.alerts.failLoadServices'));
            } finally {
                setEventsLoading(false);
            }
        };
        load();
    }, [activeTab, businessId, periodIsoRange, getAccessTokenSilently, setAlert]);

    /** Load ALL outstanding convênio receivables (PENDING CONVENIO) — these are
     *  money owed regardless of the selected period, so no date filter. */
    const loadReceivables = useCallback(async () => {
        if (!businessId) return;
        try {
            setReceivablesLoading(true);
            const token = await getAccessTokenSilently();
            const page = await getTransactions(
                businessId,
                { status: 'PENDING', method: 'CONVENIO', page: 1, perPage: 10000 },
                token
            );
            setReceivables(page.items.filter((t) => !t.deleted));
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('finance.alerts.failLoad'));
        } finally {
            setReceivablesLoading(false);
        }
    }, [businessId, getAccessTokenSilently, setAlert]);

    useEffect(() => {
        if (activeTab !== 'receivables') return;
        loadReceivables();
    }, [activeTab, loadReceivables]);

    /** Mark one convênio receivable as received (PENDING → SUCCEEDED). */
    const handleMarkReceived = async (tx: Transaction) => {
        const ok = await confirm({
            title: t('finance.receivables.markTitle'),
            message: t('finance.receivables.markConfirm', {
                name: tx.customerName || '—',
                amount: formatMoney(tx.amount ?? 0, currency),
            }),
        });
        if (!ok) return;
        try {
            setMarkBusyId(tx.id);
            const token = await getAccessTokenSilently();
            await markReceivableReceived(tx.id, token);
            setAlert('success', t('finance.receivables.marked'));
            // Drop it from the list + refresh the summary (it's now realized revenue).
            setReceivables((prev) => prev.filter((r) => r.id !== tx.id));
            reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('finance.receivables.markError'));
        } finally {
            setMarkBusyId(null);
        }
    };

    // Load transactions + appointments + services + commissions + salaries when Lançamentos tab is active
    useEffect(() => {
        if (activeTab !== 'ledger' || !businessId) return;
        const load = async () => {
            setLedgerDataLoading(true);
            try {
                const token = await getAccessTokenSilently();
                const { startIso, endIso } = periodIsoRange;
                // startDate / endDate for commission filter are "yyyy-MM-dd"
                const startYmd = startIso.split('T')[0];
                const endExclusive = endIso.split('T')[0]; // first day of next period
                // getCommissionDetails end is inclusive; subtract 1 day
                const endDate = new Date(endExclusive);
                endDate.setDate(endDate.getDate() - 1);
                const endYmd = endDate.toISOString().split('T')[0];

                // For salary payments, derive the "YYYY-MM" string from the period.
                // Month mode: one specific month. Year mode: we fetch per-month inside allLedgerRows.
                const salaryPeriodMonth = selectedMonth !== null
                    ? `${year}-${String(selectedMonth).padStart(2, '0')}`
                    : null;

                const [svcResult, apptResult, txPage, tmResult, commResult, salaryPmtResult] = await Promise.all([
                    getServicesByBusinessId(businessId, token).catch(() => [] as Service[]),
                    getAppointmentsByBusinessAndRange(businessId, startIso, endIso, token).catch(() => [] as Appointment[]),
                    getTransactions(businessId, { start: startIso, end: endIso, page: 1, perPage: 10000 }, token).catch(() => ({ items: [] as Transaction[], total: 0, page: 1, perPage: 10000 })),
                    getTeamMembersByBusinessId(businessId, token).catch(() => [] as TeamMember[]),
                    getCommissionDetails(businessId, startYmd, endYmd, null, token).catch(() => [] as CommissionRecord[]),
                    salaryPeriodMonth
                        ? getSalaryPayments(businessId, salaryPeriodMonth, token).catch(() => [] as SalaryPayment[])
                        : Promise.resolve([] as SalaryPayment[]),
                ]);
                setServices(svcResult);
                setAppointments(apptResult);
                setTransactions(txPage.items);
                setTeamMembers(tmResult);
                setCommissions(commResult);
                setSalaryPayments(salaryPmtResult);
            } catch (e) {
                setAlert('danger', e instanceof Error ? e.message : t('finance.alerts.failLoadLedger'));
            } finally {
                setLedgerDataLoading(false);
            }
        };
        load();
    }, [activeTab, businessId, periodIsoRange, year, selectedMonth, getAccessTokenSilently, setAlert]);

    // ---------- Formatting ----------

    const fmt = (n: number) => hidden ? 'R$ ••••' : formatMoney(n, currency);

    const maxAbsNet = Math.max(...summary.months.map((m) => Math.abs(m.net)), 1);

    // ---------- Period data ----------

    const periodData = useMemo(
        () => getPeriodData(summary, selectedMonth),
        [summary, selectedMonth]
    );

    const periodLabel = selectedMonth !== null
        ? `${PT_MONTH[selectedMonth - 1]} ${year}`
        : `${year}`;

    // ---------- KPI derived values ----------

    const kpiSaidas = periodData.taxes + periodData.commissions + (periodData.salaries ?? 0) + periodData.expenses;
    const kpiMargem = periodData.revenue > 0
        ? (periodData.net / periodData.revenue * 100).toFixed(1) + '%'
        : '—';

    // Estimated taxes = company default rate × revenue. INFORMATIONAL only — it's
    // an estimate (the real Simples rate varies), so it stays out of the cash KPIs.
    const taxRate = companyTaxRate ?? 0;
    const estimatedTax = taxRate > 0 ? periodData.revenue * (taxRate / 100) : 0;
    const estimatedNet = periodData.revenue - estimatedTax;

    // ---------- Period-aware category aggregation ----------

    const buildCategorySegments = useCallback(
        (type: FinanceEntryType): DonutSegment[] => {
            const active = entries.filter((e) => e.type === type && e.active);
            const map: Record<string, number> = {};
            active.forEach((e) => {
                const cat = e.category?.trim() || 'Outros';
                const val = entryAmountInPeriod(e, year, selectedMonth);
                if (val > 0) map[cat] = (map[cat] ?? 0) + val;
            });
            return Object.entries(map)
                .map(([label, value], i) => ({
                    label,
                    value,
                    color: DONUT_PALETTE[i % DONUT_PALETTE.length],
                }))
                .sort((a, b) => b.value - a.value);
        },
        [entries, selectedMonth, year]
    );

    const expenseSegments = useMemo(() => buildCategorySegments('EXPENSE'), [buildCategorySegments]);
    const creditSegments  = useMemo(() => buildCategorySegments('CREDIT'),  [buildCategorySegments]);
    const investmentSegments = useMemo(() => buildCategorySegments('INVESTMENT'), [buildCategorySegments]);

    const expensePeriodTotal    = expenseSegments.reduce((s, seg) => s + seg.value, 0);
    const creditPeriodTotal     = creditSegments.reduce((s, seg) => s + seg.value, 0);
    const investmentPeriodTotal = investmentSegments.reduce((s, seg) => s + seg.value, 0);

    // ---------- Ledger rows (tab 2) — unified source ----------

    /**
     * memberName — resolve a team-member id to a displayable name,
     * used for commission description lines.
     */
    const memberName = useCallback((id?: string | null): string => {
        if (!id) return '—';
        const tm = teamMembers.find((m) => String(m.id) === String(id));
        return tm ? `${tm.firstName ?? ''} ${tm.lastName ?? ''}`.trim() || `#${id}` : `#${id}`;
    }, [teamMembers]);

    const serviceNameById = useCallback((id?: string | null): string => {
        if (!id) return '—';
        return services.find((s) => String(s.id) === String(id))?.name ?? `#${id}`;
    }, [services]);

    /**
     * Build a lookup from appointmentId → serviceId so transaction lines can
     * show the service name.
     */
    const apptToServiceId = useMemo((): Map<string, string> => {
        const m = new Map<string, string>();
        appointments.forEach((a) => { m.set(a.id, a.serviceId); });
        return m;
    }, [appointments]);

    /**
     * All LedgerRows for the CURRENT period, regardless of filter.
     * In month mode: all individual rows sorted by date desc.
     * In year (Ano todo) mode: manual entries in detail + 3 summary rows.
     */
    const allLedgerRows = useMemo((): LedgerRow[] => {
        const isYearMode = selectedMonth === null;
        const rows: LedgerRow[] = [];

        // ── 1. Manual FinanceEntry rows ──────────────────────────────────────
        entries.forEach((e) => {
            const periodValue = entryAmountInPeriod(e, year, selectedMonth);
            if (!e.active && periodValue === 0) return; // always skip zero inactive
            // In year mode still show each manual entry (with its annual total)
            const catLabel = e.category?.trim() || 'Outros';
            const subLabel = `${catLabel} · ${t('finance.periodicity.' + e.periodicity)}`;
            rows.push({
                key: `manual-${e.id}`,
                source: 'MANUAL',
                date: e.startDate ?? null,
                typeChip: e.type === 'EXPENSE'
                    ? 'EXPENSE'
                    : e.type === 'CREDIT'
                    ? 'CREDIT'
                    : 'INVESTMENT',
                description: e.description,
                subLabel,
                amount: periodValue,
                isEntrada: e.type === 'CREDIT',
                isInvestment: e.type === 'INVESTMENT',
                manualEntry: e,
                isAuto: false,
                isInactive: !e.active,
            });
        });

        if (isYearMode) {
            // ── Year mode: add 3 summary rows ────────────────────────────────

            // Faturamento total from transactions
            const faturamentoTotal = transactions
                .filter((tx) => FATURAMENTO_STATUSES.has(tx.status) && !tx.deleted)
                .reduce((s, tx) => s + (tx.amount - (tx.refundedAmount ?? 0)), 0);
            const faturamentoCount = transactions
                .filter((tx) => FATURAMENTO_STATUSES.has(tx.status) && !tx.deleted)
                .length;
            if (faturamentoTotal > 0 || faturamentoCount > 0) {
                rows.push({
                    key: 'summary-faturamento',
                    source: 'SERVICE_SUMMARY',
                    date: null,
                    typeChip: 'SERV',
                    description: t('finance.ledger.summary.faturamento'),
                    subLabel: t('finance.ledger.summary.faturamentoPaid', { count: faturamentoCount }),
                    amount: faturamentoTotal,
                    isEntrada: true,
                    isInvestment: false,
                    isAuto: true,
                    isInactive: false,
                });
            }

            // Paid-commissions total
            const paidComm = commissions.filter((c) => !!c.paidAt);
            const commTotal = paidComm.reduce((s, c) => s + (c.amount ?? 0), 0);
            if (commTotal > 0 || paidComm.length > 0) {
                rows.push({
                    key: 'summary-commissions',
                    source: 'COMMISSION_SUMMARY',
                    date: null,
                    typeChip: 'COMISSION',
                    description: t('finance.ledger.summary.commissions'),
                    subLabel: t('finance.ledger.summary.commissionsPaid', { count: paidComm.length }),
                    amount: commTotal,
                    isEntrada: false,
                    isInvestment: false,
                    isAuto: true,
                    isInactive: false,
                });
            }

            // Paid-salaries summary (year mode: use yearSalaries from summary)
            const yearSalTotal = summary.yearSalaries ?? 0;
            if (yearSalTotal > 0) {
                rows.push({
                    key: 'summary-salaries',
                    source: 'SALARY_SUMMARY',
                    date: null,
                    typeChip: 'SALARY',
                    description: t('finance.ledger.summary.salaries'),
                    subLabel: t('finance.ledger.summary.salarySub', { year }),
                    amount: yearSalTotal,
                    isEntrada: false,
                    isInvestment: false,
                    isAuto: true,
                    isInactive: false,
                });
            }

            // Platform fees total
            const taxTotal = transactions
                .filter((tx) => FATURAMENTO_STATUSES.has(tx.status) && !tx.deleted)
                .reduce((s, tx) => s + (tx.platformFee ?? 0), 0);
            if (taxTotal > 0) {
                rows.push({
                    key: 'summary-taxes',
                    source: 'TAX_SUMMARY',
                    date: null,
                    typeChip: 'EXPENSE',
                    description: t('finance.ledger.summary.taxes'),
                    subLabel: t('finance.ledger.summary.taxesPeriodSub'),
                    amount: taxTotal,
                    isEntrada: false,
                    isInvestment: false,
                    isAuto: true,
                    isInactive: false,
                });
            }
        } else {
            // ── Month mode: individual SERVICE rows (transactions) ────────────
            transactions
                .filter((tx) => FATURAMENTO_STATUSES.has(tx.status) && !tx.deleted)
                .forEach((tx) => {
                    const net = tx.amount - (tx.refundedAmount ?? 0);
                    const svcId = tx.appointmentId ? apptToServiceId.get(tx.appointmentId) : undefined;
                    const svcName = svcId ? serviceNameById(svcId) : null;
                    const desc = svcName ?? tx.customerName ?? 'Pagamento avulso';
                    const txDate = tx.createdOn
                        ? tx.createdOn.split('T')[0]
                        : null;
                    rows.push({
                        key: `tx-${tx.id}`,
                        source: 'SERVICE',
                        date: txDate,
                        typeChip: 'SERV',
                        description: desc,
                        subLabel: tx.customerName && svcName ? tx.customerName : undefined,
                        amount: net,
                        isEntrada: true,
                        isInvestment: false,
                        isAuto: true,
                        isInactive: false,
                    });
                });

            // ── Month mode: individual COMMISSION rows (paid ones only) ───────
            commissions
                .filter((c) => !!c.paidAt)
                .forEach((c) => {
                    const desc = `Comissão — ${memberName(c.teamMemberId)}`;
                    const svcName = serviceNameById(c.serviceId);
                    const subLabel = svcName !== '—' ? svcName : undefined;
                    const commDate = c.paidAt ? c.paidAt.split('T')[0] : null;
                    rows.push({
                        key: `comm-${c.id}`,
                        source: 'COMMISSION',
                        date: commDate,
                        typeChip: 'COMISSION',
                        description: desc,
                        subLabel,
                        amount: c.amount ?? 0,
                        isEntrada: false,
                        isInvestment: false,
                        isAuto: true,
                        isInactive: false,
                    });
                });

            // ── Month mode: individual SALARY rows (paid salary payments) ────
            salaryPayments.forEach((sp) => {
                const paidDate = sp.paidAt ? sp.paidAt.split('T')[0] : null;
                rows.push({
                    key: `salary-${sp.id}`,
                    source: 'SALARY',
                    date: paidDate,
                    typeChip: 'SALARY',
                    description: `Salário — ${sp.periodMonth}`,
                    subLabel: undefined,
                    amount: sp.amount,
                    isEntrada: false,
                    isInvestment: false,
                    isAuto: true,
                    isInactive: false,
                });
            });

            // ── Month mode: platform-fee summary row (one line) ───────────────
            const taxTotal = transactions
                .filter((tx) => FATURAMENTO_STATUSES.has(tx.status) && !tx.deleted)
                .reduce((s, tx) => s + (tx.platformFee ?? 0), 0);
            if (taxTotal > 0) {
                rows.push({
                    key: 'taxes-month',
                    source: 'TAX_SUMMARY',
                    date: null,
                    typeChip: 'EXPENSE',
                    description: t('finance.ledger.summary.taxes'),
                    subLabel: t('finance.ledger.summary.taxesPeriodSub'),
                    amount: taxTotal,
                    isEntrada: false,
                    isInvestment: false,
                    isAuto: true,
                    isInactive: false,
                });
            }
        }

        return rows;
    }, [
        entries, transactions, commissions, salaryPayments, summary, year, selectedMonth,
        apptToServiceId, memberName, serviceNameById,
    ]);

    /**
     * Apply the active chip filter to allLedgerRows, then sort.
     * Sorting: auto rows (SERVICE/COMMISSION) by date desc; manual rows: date desc too;
     * summary rows always at the bottom (no date).
     */
    const visibleLedgerRows = useMemo((): LedgerRow[] => {
        let filtered = allLedgerRows;
        if (ledgerFilter !== 'all') {
            filtered = allLedgerRows.filter((r) => {
                switch (ledgerFilter) {
                    case 'CREDIT':     return r.typeChip === 'CREDIT';
                    case 'EXPENSE':    return r.typeChip === 'EXPENSE';
                    case 'INVESTMENT': return r.typeChip === 'INVESTMENT';
                    case 'COMMISSION': return r.typeChip === 'COMISSION';
                    case 'SALARY':     return r.typeChip === 'SALARY';
                    case 'SERVICE':    return r.typeChip === 'SERV';
                    default:           return true;
                }
            });
        }

        // Sort: rows with date first (desc), then rows without date (summaries)
        return [...filtered].sort((a, b) => {
            if (a.date && b.date) return b.date.localeCompare(a.date);
            if (a.date && !b.date) return -1;
            if (!a.date && b.date) return 1;
            // Both null: keep original order
            return 0;
        });
    }, [allLedgerRows, ledgerFilter]);

    // Ledger footer totals (from allLedgerRows so totals don't change with filter)
    const ledgerTotalEntradas = useMemo(
        () => allLedgerRows.filter((r) => r.isEntrada && !r.isInactive)
            .reduce((s, r) => s + r.amount, 0),
        [allLedgerRows]
    );
    const ledgerTotalSaidas = useMemo(
        () => allLedgerRows.filter((r) => !r.isEntrada && !r.isInvestment && !r.isInactive)
            .reduce((s, r) => s + r.amount, 0),
        [allLedgerRows]
    );
    const ledgerTotalInvestments = useMemo(
        () => allLedgerRows.filter((r) => r.isInvestment && !r.isInactive)
            .reduce((s, r) => s + r.amount, 0),
        [allLedgerRows]
    );
    const ledgerResultado = ledgerTotalEntradas - ledgerTotalSaidas;

    // ---------- Service faturamento ranking (transaction-based) ----------

    interface ServiceRank {
        service: Service | null; // null = "Avulso / sem serviço" bucket
        count: number;
        revenue: number;
        color: string;
    }

    const AVULSO_ID = '__avulso__';
    const AVULSO_COLOR = '#9ca3af';

    const serviceRanking: ServiceRank[] = useMemo(() => {
        // Build appointment → serviceId lookup
        const apptToService: Map<string, string> = new Map();
        appointments.forEach((appt) => {
            apptToService.set(appt.id, appt.serviceId);
        });

        // Build serviceId → rank entry (index-stable for colors)
        const map: Record<string, ServiceRank> = {};
        services.forEach((svc, i) => {
            map[svc.id] = {
                service: svc,
                count: 0,
                revenue: 0,
                color: svc.color || DONUT_PALETTE[i % DONUT_PALETTE.length],
            };
        });
        // Avulso bucket
        const avulso: ServiceRank = {
            service: null,
            count: 0,
            revenue: 0,
            color: AVULSO_COLOR,
        };

        transactions.forEach((tx) => {
            if (!FATURAMENTO_STATUSES.has(tx.status)) return;
            if (tx.deleted) return;
            // Event payments belong to the Eventos tab, not the service ranking.
            if (tx.eventId) return;
            const net = tx.amount - (tx.refundedAmount ?? 0);
            const serviceId = tx.appointmentId ? apptToService.get(tx.appointmentId) : undefined;
            if (serviceId && map[serviceId]) {
                map[serviceId].count += 1;
                map[serviceId].revenue += net;
            } else {
                avulso.count += 1;
                avulso.revenue += net;
            }
        });

        const ranked = Object.values(map).sort((a, b) => b.revenue - a.revenue);
        if (avulso.revenue > 0) {
            ranked.push(avulso);
        }
        return ranked;
    }, [services, appointments, transactions]);

    const totalServiceRevenue = serviceRanking.reduce((s, r) => s + r.revenue, 0);

    // ---------- Event faturamento ranking (transaction-based, by eventId) ----------

    interface EventRank {
        id: string;
        title: string;
        count: number;
        revenue: number;
        color: string;
    }

    const eventRanking: EventRank[] = useMemo(() => {
        const titleById = new Map<string, string>();
        events.forEach((ev) => titleById.set(String(ev.id), ev.title));
        const map: Record<string, EventRank> = {};
        transactions.forEach((tx) => {
            if (!tx.eventId) return;
            if (!FATURAMENTO_STATUSES.has(tx.status)) return;
            if (tx.deleted) return;
            const id = String(tx.eventId);
            const net = tx.amount - (tx.refundedAmount ?? 0);
            if (!map[id]) {
                const idx = Object.keys(map).length;
                map[id] = {
                    id,
                    title: titleById.get(id) ?? t('finance.events.unknown'),
                    count: 0,
                    revenue: 0,
                    color: DONUT_PALETTE[idx % DONUT_PALETTE.length],
                };
            }
            map[id].count += 1;
            map[id].revenue += net;
        });
        return Object.values(map).sort((a, b) => b.revenue - a.revenue);
    }, [events, transactions, t]);

    const totalEventRevenue = eventRanking.reduce((s, r) => s + r.revenue, 0);
    const eventDonutSegments: DonutSegment[] = eventRanking
        .filter((r) => r.revenue > 0)
        .map((r) => ({ label: r.title, value: r.revenue, color: r.color }));

    const serviceDonutSegments: DonutSegment[] = serviceRanking
        .filter((r) => r.revenue > 0)
        .map((r) => ({
            label: r.service?.name ?? t('finance.services.avulso'),
            value: r.revenue,
            color: r.color,
        }));

    // ---------- Category presets by type ----------

    const getCategoryPresets = (type: FinanceEntryType): string[] => {
        switch (type) {
            case 'EXPENSE':    return EXPENSE_CATEGORY_PRESETS;
            case 'CREDIT':     return CREDIT_CATEGORY_PRESETS;
            case 'INVESTMENT': return INVESTMENT_CATEGORY_PRESETS;
        }
    };

    // ---------- Drawer helpers ----------

    const today = new Date().toISOString().split('T')[0];

    const openCreate = () => {
        // Pre-select the type from the active Lançamentos filter (e.g. on the
        // "Investimento" filter, a new entry defaults to Investimento).
        const manualTypes: FinanceEntryType[] = ['CREDIT', 'EXPENSE', 'INVESTMENT'];
        const presetType: FinanceEntryType =
            manualTypes.includes(ledgerFilter as FinanceEntryType)
                ? (ledgerFilter as FinanceEntryType)
                : 'EXPENSE';
        setDraft({
            businessId,
            type: presetType,
            description: '',
            amount: 0,
            periodicity: 'MONTHLY',
            active: true,
            category: null,
            startDate: today,
            endDate: null,
        });
        setAmountStr('');
        setEditingId(null);
        setCategoryCustom(false);
        setCategoryCustomText('');
        setDrawerOpen(true);
    };

    const openEdit = (entry: FinanceEntry) => {
        const presets = getCategoryPresets(entry.type);
        const cat = entry.category ?? null;
        const isCustom = cat !== null && !presets.includes(cat);
        setDraft({
            id: entry.id,
            businessId,
            type: entry.type,
            description: entry.description,
            amount: entry.amount,
            periodicity: entry.periodicity,
            active: entry.active,
            category: cat,
            startDate: entry.startDate ?? today,
            endDate: entry.endDate ?? null,
        });
        setAmountStr(String(entry.amount));
        setEditingId(entry.id);
        setCategoryCustom(isCustom);
        setCategoryCustomText(isCustom ? (cat ?? '') : '');
        setDrawerOpen(true);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setDraft(null);
        setEditingId(null);
        setAmountStr('');
        setCategoryCustom(false);
        setCategoryCustomText('');
    };

    const handleSave = async () => {
        if (!draft) return;
        if (!draft.description.trim()) {
            setAlert('danger', t('finance.alerts.descriptionRequired'));
            return;
        }
        const amount = parseFloat(amountStr);
        if (isNaN(amount) || amount <= 0) {
            setAlert('danger', t('finance.alerts.amountRequired'));
            return;
        }
        if (!draft.startDate) {
            setAlert('danger', t('finance.alerts.startDateRequired'));
            return;
        }
        if (draft.endDate && draft.endDate < draft.startDate) {
            setAlert('danger', t('finance.alerts.endBeforeStart'));
            return;
        }
        const finalCategory = categoryCustom
            ? (categoryCustomText.trim() || null)
            : (draft.category ?? null);
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            await saveFinanceEntry({ ...draft, amount, category: finalCategory }, token);
            setAlert('success', editingId ? t('finance.alerts.updated') : t('finance.alerts.saved'));
            closeDrawer();
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('finance.alerts.failSave'));
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteRequest = (entry: FinanceEntry) => {
        setDeleteTarget(entry);
        closeDrawer();
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            setDeleteBusy(true);
            const token = await getAccessTokenSilently();
            await deleteFinanceEntry(deleteTarget.id, businessId, token);
            setAlert('success', t('finance.alerts.deleted'));
            setDeleteTarget(null);
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('finance.alerts.failDelete'));
        } finally {
            setDeleteBusy(false);
        }
    };

    // ---------- Shared legend ----------

    const renderLegend = (segments: DonutSegment[], total: number) => (
        <div className="finance-legend">
            {segments.map((seg, i) => {
                const pct = total > 0 ? (seg.value / total * 100).toFixed(1) : '0.0';
                return (
                    <div key={seg.label + i} className="finance-legend-row" data-cy={`donut-legend-item-${i}`}>
                        <span className="finance-legend-dot" style={{ background: seg.color }} />
                        <span className="finance-legend-label">{seg.label}</span>
                        <span className="finance-legend-value">{fmt(seg.value)}</span>
                        <span className="finance-legend-pct">{pct}%</span>
                    </div>
                );
            })}
        </div>
    );

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------

    return (
        <div className="finance-page" data-cy="finance-page">

            {/* ===== Header ===== */}
            <div className="finance-header">
                <div className="finance-header__left">
                    <h1 className="finance-title">{t('finance.title')}</h1>

                    {/* Year selector */}
                    <div className="finance-year-selector">
                        <button
                            type="button"
                            className="finance-year-selector__btn"
                            onClick={() => setYear((y) => y - 1)}
                            aria-label={t('finance.yearPrev')}
                            data-cy="finance-year-prev"
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <span className="finance-year-selector__label" data-cy="finance-year-label">{year}</span>
                        <button
                            type="button"
                            className="finance-year-selector__btn"
                            onClick={() => setYear((y) => y + 1)}
                            aria-label={t('finance.yearNext')}
                            data-cy="finance-year-next"
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>

                    {/* Month dropdown */}
                    <div className="finance-month-select-wrap">
                        <select
                            className="finance-month-select"
                            value={selectedMonth ?? ''}
                            data-cy="finance-period-select"
                            onChange={(e) => {
                                const v = e.target.value;
                                setSelectedMonth(v === '' ? null : Number(v));
                            }}
                        >
                            <option value="">{t('finance.allYear')}</option>
                            {PT_MONTH_FULL.map((m, i) => (
                                <option key={m} value={i + 1}>{m}</option>
                            ))}
                        </select>
                    </div>

                    {/* Quick presets — faster than navigating year + dropdown */}
                    {(() => {
                        const now = new Date();
                        const curY = now.getFullYear();
                        const curM = now.getMonth() + 1;
                        const lastM = curM === 1 ? 12 : curM - 1;
                        const lastY = curM === 1 ? curY - 1 : curY;
                        const isThis = year === curY && selectedMonth === curM;
                        const isLast = year === lastY && selectedMonth === lastM;
                        return (
                            <div className="finance-quick-presets">
                                <button
                                    type="button"
                                    className={`finance-preset-btn${isThis ? ' finance-preset-btn--active' : ''}`}
                                    onClick={() => { setYear(curY); setSelectedMonth(curM); }}
                                >
                                    {t('finance.thisMonth')}
                                </button>
                                <button
                                    type="button"
                                    className={`finance-preset-btn${isLast ? ' finance-preset-btn--active' : ''}`}
                                    onClick={() => { setYear(lastY); setSelectedMonth(lastM); }}
                                >
                                    {t('finance.lastMonth')}
                                </button>
                            </div>
                        );
                    })()}
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={toggleVisibility}
                        title={hidden ? t('finance.showValues') : t('finance.hideValues')}
                        data-cy="finance-eye-toggle"
                    >
                        <FontAwesomeIcon icon={hidden ? faEyeSlash : faEye} />
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={openCreate}
                        data-cy="finance-add-entry-btn"
                    >
                        <FontAwesomeIcon icon={faPlus} style={{ marginRight: '0.35rem' }} />
                        {t('finance.newEntry')}
                    </button>
                </div>
            </div>

            {/* ===== KPI row — 5 cards ===== */}
            <div className="finance-kpi-row" data-cy="finance-kpi-row">

                {/* 1 — Resultado do período (BIG) */}
                <div className={`finance-kpi finance-kpi--resultado ${periodData.net < 0 ? 'finance-kpi--neg' : 'finance-kpi--pos-accent'}`} data-cy="finance-kpi-resultado">
                    <div className="finance-kpi__label">{t('finance.kpi.resultado')}</div>
                    <div className={`finance-kpi__value finance-kpi__value--xl ${periodData.net >= 0 ? 'finance-kpi__value--pos' : 'finance-kpi__value--neg'}`} data-cy="finance-kpi-resultado-value">
                        {loading ? '—' : fmt(periodData.net)}
                    </div>
                    <div className="finance-kpi__sub">
                        <span className={`finance-kpi__badge ${periodData.net >= 0 ? 'finance-kpi__badge--green' : 'finance-kpi__badge--red'}`}>
                            {periodData.net >= 0 ? t('finance.kpi.inTheBlack') : t('finance.kpi.inTheRed')}
                        </span>
                        {' '}{periodLabel}
                    </div>
                </div>

                {/* 2 — Entradas */}
                <div className="finance-kpi finance-kpi--entradas" data-cy="finance-kpi-gross">
                    <div className="finance-kpi__label">{t('finance.kpi.entradas')}</div>
                    <div className="finance-kpi__value finance-kpi__value--pos" data-cy="finance-kpi-gross-value">
                        {loading ? '—' : fmt(periodData.revenue)}
                    </div>
                    <div className="finance-kpi__sub">{t('finance.kpi.entradasSub')}</div>
                </div>

                {/* 3 — Saídas */}
                <div className="finance-kpi finance-kpi--saidas" data-cy="finance-kpi-expenses">
                    <div className="finance-kpi__label">{t('finance.kpi.saidas')}</div>
                    <div className="finance-kpi__value finance-kpi__value--neg" data-cy="finance-kpi-expenses-value">
                        {loading ? '—' : fmt(kpiSaidas)}
                    </div>
                    <div className="finance-kpi__sub">{t('finance.kpi.saidasSub')}</div>
                </div>

                {/* 4 — Bens & Equipamentos (capex) */}
                <div className="finance-kpi finance-kpi--investment"
                    title={t('finance.kpi.investmentsTitle')}
                    data-cy="finance-kpi-investments">
                    <div className="finance-kpi__label">{t('finance.kpi.investments')}</div>
                    <div className="finance-kpi__value finance-kpi__value--investment" data-cy="finance-kpi-investments-value">
                        {loading ? '—' : fmt(periodData.investments)}
                    </div>
                    <div className="finance-kpi__sub">{t('finance.kpi.investmentsSub')}</div>
                </div>

                {/* 5 — Margem líquida */}
                <div className="finance-kpi finance-kpi--margin" data-cy="finance-kpi-margin">
                    <div className="finance-kpi__label">{t('finance.kpi.margin')}</div>
                    <div className={`finance-kpi__value ${periodData.net >= 0 ? 'finance-kpi__value--pos' : 'finance-kpi__value--neg'}`} data-cy="finance-kpi-margin-value">
                        {loading ? '—' : kpiMargem}
                    </div>
                    <div className="finance-kpi__sub">{t('finance.kpi.marginSub')}</div>
                </div>
            </div>

            {/* Estimated taxes — informational, OUTSIDE the cash flow (not in Saídas). */}
            {taxRate > 0 && (
                <div className="finance-tax-estimate" data-cy="finance-tax-estimate">
                    <span className="finance-tax-estimate__item">
                        {t('finance.taxEstimate.label', { rate: taxRate })}:{' '}
                        <strong>{loading ? '—' : fmt(estimatedTax)}</strong>
                    </span>
                    <span className="finance-tax-estimate__item">
                        {t('finance.taxEstimate.net')}:{' '}
                        <strong>{loading ? '—' : fmt(estimatedNet)}</strong>
                    </span>
                    <span className="finance-tax-estimate__note">{t('finance.taxEstimate.note')}</span>
                </div>
            )}

            {/* ===== Tab strip ===== */}
            <div className="gs-tab-strip finance-tab-strip" data-cy="finance-tab-strip">
                {([
                    { key: 'overview', label: t('finance.tabs.overview') },
                    { key: 'ledger',   label: t('finance.tabs.ledger') },
                    { key: 'services', label: t('finance.tabs.services') },
                    { key: 'events',   label: t('finance.tabs.events') },
                    ...(canReceivables
                        ? [{ key: 'receivables', label: t('finance.tabs.receivables') }]
                        : []),
                ] as { key: FinanceTab; label: string }[]).map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        className={`gs-tab ${activeTab === tab.key ? 'gs-tab--active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                        data-cy={`finance-tab-${tab.key}`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ===== Tab: Resumo (DRE + donuts + monthly table) ===== */}
            {activeTab === 'overview' && (
                <div className="finance-tab-panel">
                    {loading ? <LoadingComponent /> : (
                        <>
                            {/* DRE + donuts side by side */}
                            <div className="finance-dre-layout">

                                {/* DRE statement */}
                                <div className="finance-dre-card">
                                    <div className="finance-dre-title">{t('finance.dre.title', { period: periodLabel })}</div>
                                    <div className="finance-dre-table">

                                        {/* === ENTRADAS === */}
                                        <div className="finance-dre-row finance-dre-row--section-header">
                                            <span className="finance-dre-row__label finance-dre-row__section">{t('finance.dre.sectionEntradas')}</span>
                                        </div>

                                        <div className="finance-dre-row finance-dre-row--indent" data-cy="finance-dre-row-gross">
                                            <span className="finance-dre-row__label">{t('finance.dre.gross')}</span>
                                            <span className="finance-dre-row__value">{fmt(periodData.gross)}</span>
                                        </div>

                                        <div className="finance-dre-row finance-dre-row--indent" data-cy="finance-dre-row-credits">
                                            <span className="finance-dre-row__label">{t('finance.dre.credits')}</span>
                                            <span className="finance-dre-row__value finance-dre-row__value--pos">{fmt(periodData.credits)}</span>
                                        </div>

                                        <div className="finance-dre-row finance-dre-row--subtotal" data-cy="finance-dre-row-revenue">
                                            <span className="finance-dre-row__label">{t('finance.dre.totalEntradas')}</span>
                                            <span className="finance-dre-row__value finance-dre-row__value--pos">{fmt(periodData.revenue)}</span>
                                        </div>

                                        {/* === SAÍDAS === */}
                                        <div className="finance-dre-row finance-dre-row--section-header" style={{ marginTop: '0.5rem' }}>
                                            <span className="finance-dre-row__label finance-dre-row__section">{t('finance.dre.sectionSaidas')}</span>
                                        </div>

                                        {periodData.taxes > 0 && (
                                            <div className="finance-dre-row finance-dre-row--indent">
                                                <span className="finance-dre-row__label">{t('finance.dre.taxes')}</span>
                                                <span className="finance-dre-row__value finance-dre-row__value--neg">{fmt(periodData.taxes)}</span>
                                            </div>
                                        )}

                                        {periodData.commissions > 0 && (
                                            <div className="finance-dre-row finance-dre-row--indent">
                                                <span className="finance-dre-row__label">{t('finance.dre.commissions')}</span>
                                                <span className="finance-dre-row__value finance-dre-row__value--neg">{fmt(periodData.commissions)}</span>
                                            </div>
                                        )}

                                        {(periodData.salaries ?? 0) > 0 && (
                                            <div className="finance-dre-row finance-dre-row--indent">
                                                <span className="finance-dre-row__label">{t('finance.dre.salaries')}</span>
                                                <span className="finance-dre-row__value finance-dre-row__value--neg">{fmt(periodData.salaries)}</span>
                                            </div>
                                        )}

                                        {/* Despesas — expandable */}
                                        <div
                                            className="finance-dre-row finance-dre-row--indent finance-dre-row--expandable"
                                            onClick={() => setDreExpandExpenses((v) => !v)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => e.key === 'Enter' && setDreExpandExpenses((v) => !v)}
                                        >
                                            <span className="finance-dre-row__label">
                                                {t('finance.dre.expenses')}
                                                <FontAwesomeIcon
                                                    icon={dreExpandExpenses ? faChevronUp : faChevronDown}
                                                    style={{ marginLeft: '0.4rem', fontSize: '0.7rem', color: 'var(--gs-text-muted)' }}
                                                />
                                            </span>
                                            <span className="finance-dre-row__value finance-dre-row__value--neg">{fmt(periodData.expenses)}</span>
                                        </div>
                                        {dreExpandExpenses && expenseSegments.map((seg) => (
                                            <div key={seg.label} className="finance-dre-row finance-dre-row--sub">
                                                <span className="finance-dre-row__label">
                                                    <span className="finance-dre-dot" style={{ background: seg.color }} />
                                                    {seg.label}
                                                </span>
                                                <span className="finance-dre-row__value finance-dre-row__value--neg">{fmt(seg.value)}</span>
                                            </div>
                                        ))}

                                        <div className="finance-dre-row finance-dre-row--subtotal">
                                            <span className="finance-dre-row__label">{t('finance.dre.totalSaidas')}</span>
                                            <span className="finance-dre-row__value finance-dre-row__value--neg">
                                                {fmt(periodData.taxes + periodData.commissions + (periodData.salaries ?? 0) + periodData.expenses)}
                                            </span>
                                        </div>

                                        {/* === RESULTADO === */}
                                        <div className={`finance-dre-row finance-dre-row--total ${periodData.net < 0 ? 'finance-dre-row--total-neg' : 'finance-dre-row--total-pos'}`} data-cy="finance-dre-row-net">
                                            <span className="finance-dre-row__label">{t('finance.dre.resultado')}</span>
                                            <span className={`finance-dre-row__value finance-dre-row__value--xl ${periodData.net >= 0 ? 'finance-dre-row__value--pos' : 'finance-dre-row__value--neg'}`}>
                                                {fmt(periodData.net)}
                                            </span>
                                        </div>

                                        {/* Investimento — expandable */}
                                        <div
                                            className="finance-dre-row finance-dre-row--expandable"
                                            onClick={() => setDreExpandInvestments((v) => !v)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => e.key === 'Enter' && setDreExpandInvestments((v) => !v)}
                                        >
                                            <span className="finance-dre-row__label">
                                                {t('finance.dre.investments')}
                                                <FontAwesomeIcon
                                                    icon={dreExpandInvestments ? faChevronUp : faChevronDown}
                                                    style={{ marginLeft: '0.4rem', fontSize: '0.7rem', color: 'var(--gs-text-muted)' }}
                                                />
                                            </span>
                                            <span className="finance-dre-row__value finance-dre-row__value--investment">{fmt(periodData.investments)}</span>
                                        </div>
                                        {dreExpandInvestments && investmentSegments.map((seg) => (
                                            <div key={seg.label} className="finance-dre-row finance-dre-row--sub">
                                                <span className="finance-dre-row__label">
                                                    <span className="finance-dre-dot" style={{ background: seg.color }} />
                                                    {seg.label}
                                                </span>
                                                <span className="finance-dre-row__value finance-dre-row__value--investment">{fmt(seg.value)}</span>
                                            </div>
                                        ))}

                                        {/* Saldo acumulado */}
                                        <div className="finance-dre-row finance-dre-row--accumulated">
                                            <span className="finance-dre-row__label">{t('finance.dre.accumulated')}</span>
                                            <span className="finance-dre-row__value finance-dre-row__value--xl">{fmt(periodData.accumulatedBalance)}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Donuts column — Despesas + Créditos by category */}
                                <div className="finance-dre-charts">
                                    <div className="finance-chart-section finance-chart-section--compact">
                                        <div className="finance-donut-wrap">
                                            <DonutChart
                                                segments={expenseSegments}
                                                centerTitle={t('finance.chart.centerExpenses')}
                                                centerValue={fmt(expensePeriodTotal)}
                                                size={180}
                                            />
                                            <div className="finance-donut-period-label">{periodLabel}</div>
                                        </div>
                                        <div className="finance-chart-legend-wrap">
                                            <div className="finance-chart-legend-title">{t('finance.chart.expensesTitle')}</div>
                                            {expenseSegments.length === 0
                                                ? <p className="finance-chart-empty">{t('finance.chart.noExpenses')}</p>
                                                : renderLegend(expenseSegments, expensePeriodTotal)
                                            }
                                        </div>
                                    </div>

                                    {creditSegments.length > 0 && (
                                        <div className="finance-chart-section finance-chart-section--compact" style={{ marginTop: '0.85rem' }}>
                                            <div className="finance-donut-wrap">
                                                <DonutChart
                                                    segments={creditSegments}
                                                    centerTitle={t('finance.chart.centerCredits')}
                                                    centerValue={fmt(creditPeriodTotal)}
                                                    size={180}
                                                />
                                                <div className="finance-donut-period-label">{periodLabel}</div>
                                            </div>
                                            <div className="finance-chart-legend-wrap">
                                                <div className="finance-chart-legend-title">{t('finance.chart.creditsTitle')}</div>
                                                {renderLegend(creditSegments, creditPeriodTotal)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 12-month performance table */}
                            <div className="finance-monthly-card">
                                <div className="finance-section-header">
                                    <h2 className="finance-section-title">{t('finance.monthly.title', { year })}</h2>
                                    {selectedMonth !== null && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => setSelectedMonth(null)}
                                        >
                                            {t('finance.dre.clearFilter')}
                                        </button>
                                    )}
                                </div>
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="finance-month-table">
                                        <thead>
                                            <tr>
                                                <th>{t('finance.monthly.colMonth')}</th>
                                                <th className="num">{t('finance.monthly.colEntradas')}</th>
                                                <th className="num">{t('finance.monthly.colSaidas')}</th>
                                                <th className="num">{t('finance.monthly.colResult')}</th>
                                                <th className="num">{t('finance.monthly.colAccumulated')}</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {summary.months.map((m) => {
                                                const monthNum = Number(m.month.split('-')[1]);
                                                const isHighlighted = selectedMonth !== null
                                                    ? selectedMonth === monthNum
                                                    : isCurrentMonth(m.month, year);
                                                const pct = Math.abs(m.net) / maxAbsNet;
                                                const isNeg = m.net < 0;
                                                const saidas = m.taxes + m.commissions + (m.salaries ?? 0) + m.expenses;
                                                return (
                                                    <tr
                                                        key={m.month}
                                                        className={isHighlighted ? 'finance-month-row--highlighted' : ''}
                                                        onClick={() => setSelectedMonth(monthNum === selectedMonth ? null : monthNum)}
                                                        style={{ cursor: 'pointer' }}
                                                        data-cy={`finance-extrato-row-${m.month}`}
                                                    >
                                                        <td>
                                                            <span className={`finance-month-label${isHighlighted ? ' finance-month-label--current' : ''}`}>
                                                                {monthLabel(m.month)}
                                                                {selectedMonth === monthNum && (
                                                                    <span style={{ marginLeft: '0.4rem', fontSize: '0.65rem', fontWeight: 700, color: 'var(--gs-primary)' }}>●</span>
                                                                )}
                                                            </span>
                                                        </td>
                                                        <td className="num" style={{ color: m.revenue > 0 ? 'var(--gs-success)' : 'var(--gs-text-muted)' }}>
                                                            {m.revenue > 0 ? fmt(m.revenue) : '—'}
                                                        </td>
                                                        <td className="num" style={{ color: saidas > 0 ? 'var(--gs-danger)' : 'var(--gs-text-muted)' }}>
                                                            {saidas > 0 ? fmt(saidas) : '—'}
                                                        </td>
                                                        <td className={`net-cell ${isNeg ? 'net-cell--neg' : 'net-cell--pos'}`}>
                                                            {m.net === 0 ? '—' : fmt(m.net)}
                                                        </td>
                                                        <td className="num" style={{ color: 'var(--gs-text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                                                            {m.accumulatedBalance !== 0 ? fmt(m.accumulatedBalance) : '—'}
                                                        </td>
                                                        <td>
                                                            <div className="finance-bar-wrap" title={`Resultado: ${fmt(m.net)}`}>
                                                                <div
                                                                    className={`finance-bar ${isNeg ? 'finance-bar--neg' : ''}`}
                                                                    style={{ width: `${Math.round(pct * 100)}%` }}
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr aria-hidden className="finance-month-spacer">
                                                <td colSpan={6} style={{ height: '0.75rem', border: 'none' }} />
                                            </tr>
                                            <tr className="finance-month-total">
                                                <td style={{ borderTop: '2px solid var(--gs-divider)', fontWeight: 800 }}>
                                                    {t('finance.monthly.total', { year })}
                                                </td>
                                                <td className="num" style={{ borderTop: '2px solid var(--gs-divider)', fontWeight: 700, color: 'var(--gs-success)' }}>
                                                    {fmt(summary.yearRevenue)}
                                                </td>
                                                <td className="num" style={{ borderTop: '2px solid var(--gs-divider)', fontWeight: 700, color: 'var(--gs-danger)' }}>
                                                    {fmt(summary.yearTaxes + summary.yearCommissions + (summary.yearSalaries ?? 0) + summary.yearExpenses)}
                                                </td>
                                                <td className={`net-cell ${summary.yearNet < 0 ? 'net-cell--neg' : 'net-cell--pos'}`} style={{ borderTop: '2px solid var(--gs-divider)', fontWeight: 800 }}>
                                                    {fmt(summary.yearNet)}
                                                </td>
                                                <td className="num" style={{ borderTop: '2px solid var(--gs-divider)', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                                                    {fmt(summary.endBalance)}
                                                </td>
                                                <td style={{ borderTop: '2px solid var(--gs-divider)' }} />
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ===== Tab: Lançamentos (cash ledger — all sources) ===== */}
            {activeTab === 'ledger' && (
                <div className="finance-tab-panel">
                    {(loading || ledgerDataLoading) ? <LoadingComponent /> : (
                        <div className="finance-ledger-card">
                            {/* Ledger header + type filter */}
                            <div className="finance-ledger-header">
                                <h2 className="finance-section-title">
                                    {t('finance.ledger.title', { period: periodLabel })}
                                    {selectedMonth === null && (
                                        <span className="finance-ledger-year-note">
                                            {t('finance.ledger.yearNote')}
                                        </span>
                                    )}
                                </h2>
                                <div className="finance-ledger-filter">
                                    {([
                                        { key: 'all',        label: t('finance.ledger.filters.all') },
                                        { key: 'SERVICE',    label: t('finance.ledger.filters.service') },
                                        { key: 'CREDIT',     label: t('finance.ledger.filters.credit') },
                                        { key: 'EXPENSE',    label: t('finance.ledger.filters.expense') },
                                        { key: 'COMMISSION', label: t('finance.ledger.filters.commission') },
                                        { key: 'SALARY',     label: t('finance.ledger.filters.salary') },
                                        { key: 'INVESTMENT', label: t('finance.ledger.filters.investment') },
                                    ] as { key: LedgerFilter; label: string }[]).map((f) => (
                                        <button
                                            key={f.key}
                                            type="button"
                                            className={`finance-ledger-filter__btn ${ledgerFilter === f.key ? 'finance-ledger-filter__btn--active' : ''}`}
                                            onClick={() => setLedgerFilterSafe(f.key)}
                                            data-cy={`finance-ledger-filter-${f.key.toLowerCase()}`}
                                        >
                                            {f.label}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        style={{ marginLeft: 'auto' }}
                                        onClick={openCreate}
                                        data-cy="finance-ledger-add-btn"
                                    >
                                        <FontAwesomeIcon icon={faPlus} style={{ marginRight: '0.3rem' }} />
                                        {t('finance.ledger.add')}
                                    </button>
                                </div>
                            </div>

                            {/* Ledger table */}
                            <div style={{ overflowX: 'auto' }}>
                                <table className="finance-ledger-table" data-cy="finance-ledger-table">
                                    <thead>
                                        <tr>
                                            <th>{t('finance.ledger.colDate')}</th>
                                            <th>{t('finance.ledger.colType')}</th>
                                            <th>{t('finance.ledger.colDescription')}</th>
                                            <th className="num">{t('finance.ledger.colAmount')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleLedgerRows.length === 0 ? (
                                            <tr>
                                                <td colSpan={4} className="finance-ledger-empty">
                                                    {t('finance.ledger.empty')}
                                                </td>
                                            </tr>
                                        ) : (
                                            visibleLedgerRows.map((row) => {
                                                const isClickable = row.source === 'MANUAL' && !row.isAuto;
                                                const valueSign = row.isEntrada ? '+' : '−';
                                                const valueColorClass = row.isInvestment
                                                    ? 'finance-ledger-value--investment'
                                                    : row.isEntrada
                                                    ? 'finance-ledger-value--credit'
                                                    : 'finance-ledger-value--expense';
                                                const valueDisplay = row.amount === 0
                                                    ? '—'
                                                    : `${valueSign} ${fmt(row.amount)}`;

                                                return (
                                                    <tr
                                                        key={row.key}
                                                        className={[
                                                            'finance-ledger-row',
                                                            row.isInactive ? 'finance-ledger-row--inactive' : '',
                                                            row.isAuto ? 'finance-ledger-row--auto' : '',
                                                        ].filter(Boolean).join(' ')}
                                                        onClick={isClickable && row.manualEntry ? () => openEdit(row.manualEntry!) : undefined}
                                                        tabIndex={isClickable ? 0 : undefined}
                                                        onKeyDown={isClickable && row.manualEntry
                                                            ? (e) => e.key === 'Enter' && openEdit(row.manualEntry!)
                                                            : undefined}
                                                        style={!isClickable ? { cursor: 'default' } : undefined}
                                                        data-cy={`finance-entry-row-${row.key}`}
                                                    >
                                                        {/* Data */}
                                                        <td className="finance-ledger-date">
                                                            {row.date ? formatDate(row.date) : (
                                                                <span style={{ color: 'var(--gs-text-muted)', fontStyle: 'italic' }}>—</span>
                                                            )}
                                                        </td>

                                                        {/* Tipo chip */}
                                                        <td>
                                                            <span className={`finance-type-chip finance-type-chip--${row.typeChip.toLowerCase()}`}>
                                                                {row.typeChip === 'SERV'
                                                                    ? t('finance.ledger.chip.serv')
                                                                    : row.typeChip === 'COMISSION'
                                                                    ? t('finance.ledger.chip.comission')
                                                                    : row.typeChip === 'SALARY'
                                                                    ? t('finance.ledger.chip.salary')
                                                                    : row.typeChip === 'CREDIT'
                                                                    ? t('finance.ledger.chip.credit')
                                                                    : row.typeChip === 'EXPENSE'
                                                                    ? t('finance.ledger.chip.expense')
                                                                    : t('finance.ledger.chip.investment')}
                                                            </span>
                                                            {row.isAuto && (
                                                                <span className="finance-ledger-auto-tag">auto</span>
                                                            )}
                                                        </td>

                                                        {/* Descrição */}
                                                        <td className="finance-ledger-desc">
                                                            <span className="finance-ledger-desc__main">
                                                                {row.description}
                                                            </span>
                                                            {row.subLabel && (
                                                                <span className="finance-ledger-desc__sub">
                                                                    {row.subLabel}
                                                                </span>
                                                            )}
                                                            {row.isInactive && (
                                                                <span className="finance-ledger-inactive-tag">{t('finance.ledger.inactive')}</span>
                                                            )}
                                                        </td>

                                                        {/* Valor */}
                                                        <td className={`num finance-ledger-value ${valueColorClass}`}>
                                                            {valueDisplay}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>

                                    {/* Footer totals — consolidated on "Todos", single total per active filter. */}
                                    <tfoot>
                                        {ledgerFilter === 'all' ? (
                                            <>
                                                <tr className="finance-ledger-footer finance-ledger-footer--credit">
                                                    <td colSpan={3}>{t('finance.ledger.footer.totalEntradas')}</td>
                                                    <td className="num">+ {fmt(ledgerTotalEntradas)}</td>
                                                </tr>
                                                <tr className="finance-ledger-footer finance-ledger-footer--expense">
                                                    <td colSpan={3}>{t('finance.ledger.footer.totalSaidas')}</td>
                                                    <td className="num">− {fmt(ledgerTotalSaidas)}</td>
                                                </tr>
                                                <tr className="finance-ledger-footer finance-ledger-footer--investment">
                                                    <td colSpan={3}>{t('finance.ledger.footer.totalInvestments')}</td>
                                                    <td className="num">− {fmt(ledgerTotalInvestments)}</td>
                                                </tr>
                                                <tr className={`finance-ledger-footer finance-ledger-footer--resultado ${ledgerResultado >= 0 ? 'finance-ledger-footer--pos' : 'finance-ledger-footer--neg'}`}>
                                                    <td colSpan={3}>{t('finance.ledger.footer.resultado')}</td>
                                                    <td className="num">{ledgerResultado >= 0 ? '+' : '−'} {fmt(Math.abs(ledgerResultado))}</td>
                                                </tr>
                                            </>
                                        ) : (() => {
                                            // Single-filter view: only the total of the active tab.
                                            const total = visibleLedgerRows
                                                .filter((r) => !r.isInactive)
                                                .reduce((s, r) => s + r.amount, 0);
                                            const isEntrada = ledgerFilter === 'CREDIT' || ledgerFilter === 'SERVICE';
                                            const isInvest = ledgerFilter === 'INVESTMENT';
                                            const cls = isInvest
                                                ? 'finance-ledger-footer--investment'
                                                : isEntrada
                                                ? 'finance-ledger-footer--credit'
                                                : 'finance-ledger-footer--expense';
                                            const sign = isEntrada ? '+' : '−';
                                            const labels: Record<string, string> = {
                                                SERVICE: t('finance.ledger.footer.totalService'),
                                                CREDIT: t('finance.ledger.footer.totalCredit'),
                                                EXPENSE: t('finance.ledger.footer.totalExpense'),
                                                COMMISSION: t('finance.ledger.footer.totalCommission'),
                                                SALARY: t('finance.ledger.footer.totalSalary'),
                                                INVESTMENT: t('finance.ledger.footer.totalInvestment'),
                                            };
                                            return (
                                                <tr className={`finance-ledger-footer ${cls}`}>
                                                    <td colSpan={3}>{labels[ledgerFilter] ?? 'Total'}</td>
                                                    <td className="num">{sign} {fmt(total)}</td>
                                                </tr>
                                            );
                                        })()}
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ===== Tab: Serviços ===== */}
            {activeTab === 'services' && (
                <div className="finance-tab-panel">
                    {servicesLoading ? (
                        <LoadingComponent />
                    ) : totalServiceRevenue === 0 ? (
                        <div className="finance-empty" style={{ background: 'var(--gs-surface)', borderRadius: 'var(--gs-radius-lg)', padding: '3rem 1rem', boxShadow: 'var(--gs-shadow-card)' }}>
                            <strong>{t('finance.services.empty')}</strong>
                            {t('finance.services.emptyHint')}
                        </div>
                    ) : (
                        <>
                            {(() => {
                                // Highlights: only named services (exclude avulso), only those with revenue > 0
                                const withRevenue = serviceRanking.filter((r) => r.service !== null && r.revenue > 0);
                                if (withRevenue.length < 2) return null;
                                const top  = withRevenue[0];
                                const last = withRevenue[withRevenue.length - 1];
                                return (
                                    <div className="finance-svc-highlights">
                                        <div className="finance-svc-highlight finance-svc-highlight--top">
                                            <div>
                                                <div className="finance-svc-highlight__tag">{t('finance.services.topEarner')}</div>
                                                <div className="finance-svc-highlight__name">{top.service!.name}</div>
                                                <div className="finance-svc-highlight__val">{fmt(top.revenue)} · {t('finance.services.payments', { count: top.count })}</div>
                                            </div>
                                        </div>
                                        <div className="finance-svc-highlight finance-svc-highlight--bot">
                                            <div>
                                                <div className="finance-svc-highlight__tag">{t('finance.services.leastEarner')}</div>
                                                <div className="finance-svc-highlight__name">{last.service!.name}</div>
                                                <div className="finance-svc-highlight__val">{fmt(last.revenue)} · {t('finance.services.payments', { count: last.count })}</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                            <div className="finance-chart-section finance-chart-section--services">
                                <div className="finance-svc-ranking">
                                    <div className="finance-chart-legend-title" style={{ marginBottom: '0.25rem' }}>
                                        {t('finance.services.rankTitle', { period: periodLabel })}
                                    </div>
                                    <div style={{ fontSize: '0.72rem', color: 'var(--gs-text-muted)', marginBottom: '0.75rem', lineHeight: 1.4 }}>
                                        {t('finance.services.rankHint')}
                                    </div>
                                    {serviceRanking.map((r) => {
                                        const key = r.service ? r.service.id : AVULSO_ID;
                                        const name = r.service?.name ?? t('finance.services.avulso');
                                        const pct = totalServiceRevenue > 0 ? (r.revenue / totalServiceRevenue * 100) : 0;
                                        const isZero = r.revenue === 0;
                                        return (
                                            <div
                                                key={key}
                                                className="finance-svc-bar-row"
                                                style={isZero ? { opacity: 0.4 } : undefined}
                                            >
                                                <span className="finance-svc-color-dot" style={{ background: r.color }} />
                                                <span className="finance-svc-bar-name">{name}</span>
                                                <div className="finance-svc-bar-track">
                                                    <div className="finance-svc-bar-fill" style={{ width: `${pct}%`, background: r.color }} />
                                                </div>
                                                <span className="finance-svc-bar-meta">
                                                    {isZero ? '—' : `${t('finance.services.payments', { count: r.count })} · ${fmt(r.revenue)}`}
                                                </span>
                                                <span className="finance-svc-bar-pct">{pct.toFixed(1)}%</span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="finance-donut-wrap">
                                    <DonutChart
                                        segments={serviceDonutSegments}
                                        centerTitle={t('finance.services.centerFaturamento')}
                                        centerValue={fmt(totalServiceRevenue)}
                                        size={210}
                                    />
                                    <div className="finance-donut-period-label">{periodLabel}</div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ===== Tab: Eventos (faturamento por evento) ===== */}
            {activeTab === 'events' && (
                <div className="finance-tab-panel">
                    {eventsLoading ? (
                        <LoadingComponent />
                    ) : totalEventRevenue === 0 ? (
                        <div className="finance-empty" style={{ background: 'var(--gs-surface)', borderRadius: 'var(--gs-radius-lg)', padding: '3rem 1rem', boxShadow: 'var(--gs-shadow-card)' }}>
                            <strong>{t('finance.events.empty')}</strong>
                            {t('finance.events.emptyHint')}
                        </div>
                    ) : (
                        <div className="finance-chart-section finance-chart-section--services">
                            <div className="finance-svc-ranking">
                                <div className="finance-chart-legend-title" style={{ marginBottom: '0.25rem' }}>
                                    {t('finance.events.rankTitle', { period: periodLabel })}
                                </div>
                                <div style={{ fontSize: '0.72rem', color: 'var(--gs-text-muted)', marginBottom: '0.75rem', lineHeight: 1.4 }}>
                                    {t('finance.events.rankHint')}
                                </div>
                                {eventRanking.map((r) => {
                                    const pct = totalEventRevenue > 0 ? (r.revenue / totalEventRevenue * 100) : 0;
                                    return (
                                        <div key={r.id} className="finance-svc-bar-row">
                                            <span className="finance-svc-color-dot" style={{ background: r.color }} />
                                            <span className="finance-svc-bar-name">{r.title}</span>
                                            <div className="finance-svc-bar-track">
                                                <div className="finance-svc-bar-fill" style={{ width: `${pct}%`, background: r.color }} />
                                            </div>
                                            <span className="finance-svc-bar-meta">
                                                {`${t('finance.services.payments', { count: r.count })} · ${fmt(r.revenue)}`}
                                            </span>
                                            <span className="finance-svc-bar-pct">{pct.toFixed(1)}%</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="finance-donut-wrap">
                                <DonutChart
                                    segments={eventDonutSegments}
                                    centerTitle={t('finance.events.centerFaturamento')}
                                    centerValue={fmt(totalEventRevenue)}
                                    size={210}
                                />
                                <div className="finance-donut-period-label">{periodLabel}</div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ===== Tab: Contas a receber (convênio receivables) ===== */}
            {activeTab === 'receivables' && (
                <div className="finance-tab-panel">
                    {receivablesLoading ? (
                        <LoadingComponent />
                    ) : receivables.length === 0 ? (
                        <div className="empty-hint" style={{ padding: '1.5rem 0' }}>
                            {t('finance.receivables.empty')}
                        </div>
                    ) : (
                        <>
                            <div className="finance-receivables-head" style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                                marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem',
                            }}>
                                <span style={{ color: 'var(--gs-text-muted)', fontSize: '0.9rem' }}>
                                    {t('finance.receivables.hint')}
                                </span>
                                <strong style={{ fontSize: '1.05rem' }}>
                                    {t('finance.receivables.totalPending')}: {fmt(
                                        receivables.reduce((s, r) => s + (r.amount ?? 0), 0)
                                    )}
                                </strong>
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                                <table className="finance-ledger-table" data-cy="finance-receivables-table">
                                    <thead>
                                        <tr>
                                            <th>{t('finance.receivables.colDate')}</th>
                                            <th>{t('finance.receivables.colCustomer')}</th>
                                            <th>{t('finance.receivables.colDescription')}</th>
                                            <th className="num">{t('finance.receivables.colAmount')}</th>
                                            <th className="num">{t('finance.receivables.colAction')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {receivables.map((r) => (
                                            <tr key={r.id} data-cy={`receivable-row-${r.id}`}>
                                                <td className="finance-ledger-date">{new Date(r.createdOn).toLocaleDateString()}</td>
                                                <td>{r.customerName || '—'}</td>
                                                <td>{r.description || '—'}</td>
                                                <td className="num" style={{ whiteSpace: 'nowrap' }}>{fmt(r.amount ?? 0)}</td>
                                                <td className="num">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-primary"
                                                        data-cy={`receivable-mark-${r.id}`}
                                                        disabled={markBusyId === r.id}
                                                        onClick={() => handleMarkReceived(r)}
                                                    >
                                                        {markBusyId === r.id
                                                            ? t('common.working')
                                                            : t('finance.receivables.markReceived')}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ===== Drawer form ===== */}
            <Drawer
                open={drawerOpen}
                onClose={closeDrawer}
                title={editingId ? t('finance.drawer.titleEdit') : t('finance.drawer.titleNew')}
                subtitle={editingId ? t('finance.drawer.subtitleEdit') : t('finance.drawer.subtitleNew')}
                size="md"
                footer={
                    <>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={saving}
                            data-cy="finance-save-btn"
                        >
                            {saving ? t('common.working') : t('common.save')}
                        </button>
                        {editingId && draft && (
                            <button
                                type="button"
                                className="btn btn-outline-danger"
                                disabled={saving}
                                onClick={() => {
                                    const target = entries.find((e) => e.id === editingId);
                                    if (target) handleDeleteRequest(target);
                                }}
                                data-cy="finance-delete-btn"
                            >
                                {t('common.delete')}
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={closeDrawer}
                            disabled={saving}
                        >
                            {t('common.cancel')}
                        </button>
                    </>
                }
            >
                {draft && (() => {
                    const presets = getCategoryPresets(draft.type);
                    const selectValue = categoryCustom ? '__custom__' : (draft.category ?? '');
                    const isOnce = draft.periodicity === 'ONCE';
                    const isRecurring = !isOnce;
                    return (
                        <div className="finance-drawer-form" data-cy="finance-entry-drawer">

                            {/* Type toggle */}
                            <div className="form-group">
                                <label>{t('finance.drawer.labelType')}</label>
                                <div className="finance-type-toggle">
                                    {(['EXPENSE', 'CREDIT', 'INVESTMENT'] as FinanceEntryType[]).map((typeKey) => (
                                        <button
                                            key={typeKey}
                                            type="button"
                                            className={`finance-type-btn finance-type-btn--${typeKey.toLowerCase()} ${draft.type === typeKey ? 'finance-type-btn--active' : ''}`}
                                            onClick={() => {
                                                setDraft({ ...draft, type: typeKey, category: null });
                                                setCategoryCustom(false);
                                                setCategoryCustomText('');
                                            }}
                                            data-cy={`finance-type-btn-${typeKey.toLowerCase()}`}
                                        >
                                            {t('finance.type.' + typeKey)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Category */}
                            <div className="form-group">
                                <label>{t('finance.drawer.labelCategory')}</label>
                                <select
                                    className="gs-select"
                                    value={selectValue}
                                    data-cy="finance-category-input"
                                    onChange={(e) => {
                                        if (e.target.value === '__custom__') {
                                            setCategoryCustom(true);
                                            setDraft({ ...draft, category: null });
                                        } else {
                                            setCategoryCustom(false);
                                            setCategoryCustomText('');
                                            setDraft({ ...draft, category: e.target.value || null });
                                        }
                                    }}
                                >
                                    <option value="">{t('finance.drawer.selectCategory')}</option>
                                    {presets.map((p) => (
                                        <option key={p} value={p}>{p}</option>
                                    ))}
                                    <option value="__custom__">{t('finance.drawer.categoryOther')}</option>
                                </select>
                                {categoryCustom && (
                                    <input
                                        type="text"
                                        className="gs-input"
                                        style={{ marginTop: '0.4rem' }}
                                        placeholder={t('finance.drawer.categoryPlaceholder')}
                                        value={categoryCustomText}
                                        onChange={(e) => setCategoryCustomText(e.target.value)}
                                        autoFocus
                                    />
                                )}
                            </div>

                            {/* Description */}
                            <div className="form-group">
                                <label>{t('finance.drawer.labelDescription')} <span className="gs-required">*</span></label>
                                <input
                                    type="text"
                                    className="gs-input"
                                    placeholder={t('finance.drawer.descriptionPlaceholder')}
                                    value={draft.description}
                                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                                    data-cy="finance-description-input"
                                />
                            </div>

                            {/* Amount + Periodicity */}
                            <div className="gs-form-grid">
                                <div className="form-group">
                                    <label>{t('finance.drawer.labelAmount')} <span className="gs-required">*</span></label>
                                    <MoneyInput
                                        currency={currency}
                                        value={amountStr}
                                        onChange={setAmountStr}
                                        placeholder="0.00"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('finance.drawer.labelPeriodicity')}</label>
                                    <select
                                        className="gs-select"
                                        value={draft.periodicity}
                                        data-cy="finance-periodicity-input"
                                        onChange={(e) => {
                                            const p = e.target.value as FinancePeriodicity;
                                            setDraft({
                                                ...draft,
                                                periodicity: p,
                                                endDate: p === 'ONCE' ? null : draft.endDate,
                                            });
                                        }}
                                    >
                                        {(['ONCE', 'MONTHLY', 'QUARTERLY', 'SEMIANNUAL', 'ANNUAL'] as FinancePeriodicity[]).map((k) => (
                                            <option key={k} value={k}>{t('finance.periodicity.' + k)}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Start date — always required */}
                            <div className="form-group">
                                <label>
                                    {isOnce ? t('finance.drawer.labelDate') : t('finance.drawer.labelStart')} <span className="gs-required">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="gs-input"
                                    value={draft.startDate}
                                    onChange={(e) => setDraft({ ...draft, startDate: e.target.value || today })}
                                />
                            </div>

                            {/* End date — only for recurring */}
                            {isRecurring && (
                                <div className="form-group">
                                    <label>{t('finance.drawer.labelEnd')}</label>
                                    <input
                                        type="date"
                                        className="gs-input"
                                        value={draft.endDate ?? ''}
                                        min={draft.startDate}
                                        onChange={(e) => setDraft({ ...draft, endDate: e.target.value || null })}
                                    />
                                    <span className="finance-drawer-hint">
                                        {t('finance.drawer.endHint')}
                                    </span>
                                </div>
                            )}

                            {/* Active toggle */}
                            <div className="form-group">
                                <label className="gs-inline-check" htmlFor="finance-active">
                                    <input
                                        id="finance-active"
                                        type="checkbox"
                                        className="gs-inline-check__box"
                                        checked={draft.active}
                                        onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                                    />
                                    <span className="gs-inline-check__label">{t('finance.drawer.labelActive')}</span>
                                </label>
                            </div>
                        </div>
                    );
                })()}
            </Drawer>

            {/* Delete confirmation */}
            <ConfirmModal
                open={!!deleteTarget}
                title={t('finance.delete.title')}
                danger
                busy={deleteBusy}
                confirmLabel={t('common.delete')}
                cancelLabel={t('common.cancel')}
                onConfirm={confirmDelete}
                onCancel={() => { if (!deleteBusy) setDeleteTarget(null); }}
                message={
                    deleteTarget && (
                        <>
                            {t('finance.delete.message', { description: deleteTarget.description }).split('\n').map((line, i) => (
                                <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>
                            ))}
                        </>
                    )
                }
            />
        </div>
    );
};

export default FinanceComponent;
